import {
  asStableCallback,
  useStableCallback,
} from "@bezoned/shared/hooks/use-stable-callback.js";
import { UserAvailabilityOptions } from "@bezoned/shared/types/user-presence.js";
import type { UserAvailability } from "@bezoned/shared/types/user-presence.js";
import {
  Button,
  Dropdown,
  Field,
  Input,
  Option,
} from "@fluentui/react-components";
import { useForm, useStore } from "@tanstack/react-form";
import { useMemo } from "react";
import { z } from "zod";

const TABLE_NAME_MAX_LENGTH = 250;

const tableNameSchema = z
  .string()
  .min(1, "Table name is required")
  .max(TABLE_NAME_MAX_LENGTH, "Table name must be 250 characters or less")
  .refine((value) => value.trim().length > 0, "Table name is required");

const DEFAULT_STATUS_OPTIONS: {
  value: UserAvailability | "none";
  label: string;
}[] = [
  { label: "Available", value: UserAvailabilityOptions.Available },
  { label: "Away", value: UserAvailabilityOptions.Away },
  { label: "Busy", value: UserAvailabilityOptions.Busy },
  { label: "Do not disturb", value: UserAvailabilityOptions.DoNotDisturb },
  { label: "None", value: "none" },
];

interface TableSettingsGeneralTabProps {
  existingTableNames: string[];
  initialName: string;
  initialDefaultStatus: UserAvailability | null;
  isTeamAdmin: boolean;
  onSave: (value: {
    name: string;
    defaultStatus: UserAvailability | undefined;
  }) => Promise<void>;
}

const getNameValidationMessage = (
  value: string,
  existingTableNames: string[]
): string | null => {
  const parsed = tableNameSchema.safeParse(value);

  if (!parsed.success) {
    const firstIssue = parsed.error.issues[0];
    return firstIssue?.message ?? "Table name is required";
  }

  const trimmed = value.trim();

  if (
    existingTableNames.some(
      (name) => name.toLowerCase() === trimmed.toLowerCase()
    )
  ) {
    return "A table with this name already exists";
  }

  return null;
};

const toDropdownValue = (status: UserAvailability | null): string =>
  status ?? "none";

const fromDropdownValue = (value: string): UserAvailability | undefined =>
  value === "none" ? undefined : (value as UserAvailability);

export const TableSettingsGeneralTab = ({
  existingTableNames,
  initialName,
  initialDefaultStatus,
  isTeamAdmin,
  onSave,
}: TableSettingsGeneralTabProps) => {
  const form = useForm({
    defaultValues: {
      defaultStatus: toDropdownValue(initialDefaultStatus),
      name: initialName,
    },
    onSubmit: async ({ value }) => {
      await onSave({
        defaultStatus: fromDropdownValue(value.defaultStatus),
        name: value.name.trim(),
      });
    },
  });

  const name = useStore(form.store, (state) => state.values.name);
  const defaultStatus = useStore(
    form.store,
    (state) => state.values.defaultStatus
  );
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  const nameValidationMessage = useMemo(
    () =>
      isTeamAdmin ? getNameValidationMessage(name, existingTableNames) : null,
    [isTeamAdmin, name, existingTableNames]
  );

  const hasChanges =
    name.trim() !== initialName ||
    defaultStatus !== toDropdownValue(initialDefaultStatus);

  const canSubmit =
    hasChanges && nameValidationMessage === null && !isSubmitting;
  const handleClick189 = useStableCallback(() => {
    if (canSubmit) {
      form.handleSubmit();
    }
  });
  const handleSubmit123 = useStableCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!canSubmit) {
      return;
    }

    form.handleSubmit();
  });

  return (
    <div className="flex w-full flex-1 flex-col gap-8">
      <form className="flex w-full flex-col gap-4" onSubmit={handleSubmit123}>
        <form.Field name="name">
          {(field) => (
            <Field
              className="w-full"
              label="Table name"
              validationMessage={nameValidationMessage}
              validationState={nameValidationMessage ? "error" : "none"}
            >
              <Input
                className="w-full"
                disabled={isSubmitting || !isTeamAdmin}
                onChange={asStableCallback((event) => {
                  field.handleChange(event.target.value);
                })}
                value={field.state.value}
              />
              {isTeamAdmin ? null : (
                <p className="mt-1 text-xs text-slate-500">
                  Only team admin can change name
                </p>
              )}
            </Field>
          )}
        </form.Field>

        <form.Field name="defaultStatus">
          {(field) => (
            <Field
              className="w-full"
              hint="When user moves to this table, their MS Teams status will be automatically updated"
              label="Default status"
            >
              <Dropdown
                disabled={isSubmitting}
                onOptionSelect={asStableCallback((_event, data) => {
                  if (data.optionValue) {
                    field.handleChange(data.optionValue);
                  }
                })}
                selectedOptions={[field.state.value]}
                value={
                  DEFAULT_STATUS_OPTIONS.find(
                    (option) => option.value === field.state.value
                  )?.label ?? "None"
                }
              >
                {DEFAULT_STATUS_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          )}
        </form.Field>
      </form>

      <div className="mt-auto flex items-center justify-end gap-3">
        <Button
          appearance="primary"
          disabled={!canSubmit}
          onClick={handleClick189}
          type="button"
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};
